import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import theme from "./theme";

import { AuthProvider, useAuth } from "./contexts/AuthContext";

import ProtectedRoute from "./components/ProtectedRoute";
import MainLayout from "./layouts/MainLayout";

import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import Dashboard from "./pages/Dashboard";
import DocumentManagement from "./pages/documents/DocumentManagement";
import NDAManagement from "./pages/nda/NDAManagement";
import NDATypeManagement from "./pages/admin/NDATypeManagement";
import CertificateManagement from "./pages/certificates/CertificateManagement";
import AdminPanel from "./pages/admin/AdminPanel";
import UserApprovalManagement from "./pages/admin/UserApprovalManagement";
import UserManagement from "./pages/admin/UserManagement";
import ResearchProjectManagement from "./pages/research/ResearchProjectManagement";
import ProjectDocumentsManagement from "./pages/research/ProjectDocumentsManagement";
import PublicResearchSearch from "./pages/search/PublicResearchSearch";
import Profile from "./pages/profile/Profile";
import LegalNDAManagement from "./pages/legal/LegalNDAManagement";
import NotFound from "./pages/NotFound";

// Componente para rutas públicas que redirige a dashboard si ya está autenticado
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  // Mientras está cargando, mostrar null (React mantiene la página)
  if (loading) {
    return null;
  }

  // Si ya está autenticado, redirige al dashboard
  if (isAuthenticated()) {
    return <Navigate to="/" replace />;
  }

  // Si no está autenticado, mostrar el componente (login, register)
  return children;
};

const LayoutWrapper = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated() ? <MainLayout>{children}</MainLayout> : children;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPassword />
              </PublicRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PublicRoute>
                <ResetPassword />
              </PublicRoute>
            }
          />

          {/* Protected routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <Dashboard />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/documents"
            element={
              <ProtectedRoute
                requiredRole={["admin", "collaborator", "researcher"]}
              >
                <LayoutWrapper>
                  <DocumentManagement />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/certificates"
            element={
              <ProtectedRoute requiredRole="admin">
                <LayoutWrapper>
                  <CertificateManagement />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/ndas"
            element={
              <ProtectedRoute requiredRole={["admin", "researcher"]}>
                <LayoutWrapper>
                  <NDAManagement />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredRole="admin">
                <LayoutWrapper>
                  <AdminPanel />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/users"
            element={
              <ProtectedRoute requiredRole="admin">
                <LayoutWrapper>
                  <UserApprovalManagement />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/users/all"
            element={
              <ProtectedRoute requiredRole="admin">
                <LayoutWrapper>
                  <UserManagement />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/nda-types"
            element={
              <ProtectedRoute requiredRole="admin">
                <LayoutWrapper>
                  <NDATypeManagement />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/research-projects"
            element={
              <ProtectedRoute
                requiredRole={[
                  "admin",
                  "collaborator",
                  "student",
                  "researcher",
                ]}
              >
                <LayoutWrapper>
                  <ResearchProjectManagement />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/research-projects/:projectId/documents"
            element={
              <ProtectedRoute requiredRole={["admin", "researcher"]}>
                <LayoutWrapper>
                  <ProjectDocumentsManagement />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          {/* Legal routes */}
          <Route
            path="/legal/ndas"
            element={
              <ProtectedRoute requiredRole="legal">
                <LayoutWrapper>
                  <LegalNDAManagement />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          {/* Profile route */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <Profile />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          {/* Public search route */}
          <Route path="/search" element={<PublicResearchSearch />} />

          {/* Fallback routes */}
          <Route path="/404" element={<NotFound />} />
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Routes>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
